import React from 'react'

import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import {
  Bell as BellIcon,
  CheckCircle as CheckCircleIcon,
  Moon as MoonIcon,
  Trash2 as Trash2Icon,
  UserCheck as UserCheckIcon,
} from 'react-feather'

import { BulkActions } from 'components'
import { ProfileSelect } from './components'

import useBulkSelection from 'hooks/useBulkSelection'

import * as service from 'service'
import constants from 'constants/index'
import useStyles from './styles'

const UsersBulkActions = ({ profiles = [], refresh }) => {
  const { items, selected, setSelected } = useBulkSelection()

  const classes = useStyles()

  const isActive = (userCompany) =>
    userCompany?.status === constants.userCompanies.ACTIVE

  const isInactive = (userCompany) =>
    userCompany?.status === constants.userCompanies.INACTIVE

  const describeUserCompany = (userCompany) => ({
    name: userCompany?.user?.name,
    subtitle: userCompany?.user?.email,
  })

  const renderProfileSelect = ({ values, setValue, errors }) => (
    <ProfileSelect
      profiles={profiles}
      value={values?.profileId}
      error={errors?.profileId}
      onChange={(profileId) => setValue('profileId', profileId)}
    />
  )

  const validateProfile = (values) =>
    isEmpty(values?.profileId?.toString())
      ? { profileId: 'Perfil não pode ficar em branco.' }
      : {}

  const actions = [
    {
      key: 'changeProfile',
      label: 'Alterar perfil',
      icon: <UserCheckIcon size={16} />,
      permission: constants.permissions.USERS.EDIT,
      isEnabled: (selectedUserCompanies) =>
        selectedUserCompanies.some(isActive),
      disabledHint: 'Nenhum usuário ativo selecionado',
      title: 'Alterar perfil dos usuários',
      confirmText: 'Alterar',
      renderFields: renderProfileSelect,
      validateFields: validateProfile,
      request: ({ ids, values }) =>
        service.dponet.usersCompanies.bulk.update({
          ids,
          profileId: values?.profileId,
        }),
      successMessage: 'Perfil dos usuários alterado com sucesso!',
      errorMessage: 'Falha ao alterar o perfil dos usuários!',
    },
    {
      key: 'notify',
      label: 'Notificar',
      icon: <BellIcon size={16} />,
      hint: 'Notificar Treinamentos Pendentes',
      permission: constants.permissions.TRAINNING_JOURNEYS.MANAGE,
      isEnabled: (selectedUserCompanies) =>
        selectedUserCompanies.some(isActive),
      disabledHint: 'Nenhum usuário ativo selecionado',
      title: 'Notificar treinamentos pendentes',
      confirmText: 'Notificar',
      warning:
        'Usuários que já concluíram os treinamentos ou já foram notificados hoje serão ignorados.',
      itemId: (userCompany) => userCompany?.user?.id,
      request: ({ ids }) =>
        service.dponet.trainningJourneys.notifyPendingTrainnings({
          userIds: ids,
        }),
      successMessage:
        'Será enviado um e-mail de notificação para os usuários sobre seus treinamentos pendentes.',
      errorMessage: 'Falha ao enviar notificações!',
    },
    {
      key: 'activate',
      label: 'Ativar',
      icon: <CheckCircleIcon size={16} />,
      permission: constants.permissions.USERS.EDIT,
      isEnabled: (selectedUserCompanies) =>
        selectedUserCompanies.some(isInactive),
      disabledHint: 'Nenhum usuário inativo selecionado',
      title: 'Ativar usuários',
      confirmText: 'Ativar',
      warning: 'Usuários que já estão ativos serão ignorados.',
      renderFields: renderProfileSelect,
      validateFields: validateProfile,
      request: ({ ids, values }) =>
        service.dponet.usersCompanies.bulk.changeStatus({
          ids,
          status: constants.userCompanies.ACTIVE,
          profileId: values?.profileId,
        }),
      successMessage: 'Usuários ativados com sucesso!',
      errorMessage: 'Falha ao ativar usuários!',
    },
    {
      key: 'inactivate',
      label: 'Inativar',
      icon: <MoonIcon size={16} />,
      permission: constants.permissions.USERS.EDIT,
      isEnabled: (selectedUserCompanies) =>
        selectedUserCompanies.some(isActive),
      disabledHint: 'Nenhum usuário ativo selecionado',
      title: 'Inativar usuários',
      confirmText: 'Inativar',
      warning: 'Usuários que já estão inativos serão ignorados.',
      request: ({ ids }) =>
        service.dponet.usersCompanies.bulk.changeStatus({
          ids,
          status: constants.userCompanies.INACTIVE,
        }),
      successMessage: 'Usuários inativados com sucesso!',
      errorMessage: 'Falha ao inativar usuários!',
    },
    {
      key: 'delete',
      label: 'Excluir',
      icon: <Trash2Icon size={16} />,
      className: classes.deleteButton,
      permission: constants.permissions.USERS.SOFT_DELETE,
      disabledHint: 'Nenhum usuário selecionado',
      title: 'Excluir usuários',
      confirmText: 'Excluir',
      request: ({ ids }) =>
        service.dponet.usersCompanies.bulk.softDelete({ ids }),
      successMessage: 'Usuários excluídos com sucesso!',
      errorMessage: 'Falha ao excluir usuários!',
    },
  ]

  return (
    <BulkActions
      items={items}
      selected={selected}
      setSelected={setSelected}
      actions={actions}
      describeItem={describeUserCompany}
      itemLabel={{ singular: 'usuário', plural: 'usuários' }}
      refresh={refresh}
    />
  )
}

UsersBulkActions.propTypes = {
  profiles: PropTypes.array,
  refresh: PropTypes.func,
}

UsersBulkActions.defaultProps = {
  refresh: () => {},
}

export default UsersBulkActions
